import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'platform/apollo';

import type {
  UpdateCustomerDetailsMutation,
  UpdatePasswordMutation,
  UpdatePrimaryPhoneNumberMutation,
} from './__generated__/useCustomerDetails';
import {
  CustomerDetailsDocument,
  UpdateCustomerDetailsDocument,
  UpdatePasswordDocument,
  UpdatePrimaryPhoneNumberDocument,
} from './__generated__/useCustomerDetails';

gql`
  query CustomerDetails {
    customerService {
      getCurrentCustomer {
        customer {
          uuid
          firstName
          lastName
          primaryPhoneNumber {
            number {
              value
            }
          }
          primaryEmail {
            address {
              value
            }
          }
        }
      }
    }
  }
`;

gql`
  mutation UpdateCustomerDetails($firstName: String!, $lastName: String!) {
    customerService {
      updateCurrent(input: { firstName: $firstName, lastName: $lastName }) {
        updatedCustomer {
          uuid
          firstName
          lastName
        }
      }
    }
  }
`;

gql`
  mutation UpdatePrimaryPhoneNumber($newPhoneNumber: String!) {
    customerService {
      setPrimaryPhoneNumber(input: { number: $newPhoneNumber }) {
        customer {
          uuid
          primaryPhoneNumber {
            number {
              value
            }
          }
        }
      }
    }
  }
`;

gql`
  mutation UpdatePassword($currentPassword: String!, $newPassword: String!) {
    webPassword {
      updatePassword(input: { currentPassword: $currentPassword, newPassword: $newPassword }) {
        _empty
      }
    }
  }
`;

export const useCustomerDetails = () => {
  const { data, loading, refetch } = useQuery(CustomerDetailsDocument, {
    fetchPolicy: 'no-cache',
  });

  return {
    data,
    loading,
    refetch,
  };
};

export const useUpdateCustomerDetails = (
  callback?: (data: UpdateCustomerDetailsMutation) => void,
) => {
  const [updateCustomerDetails, { loading, error }] = useMutation(UpdateCustomerDetailsDocument, {
    onCompleted: callback,
  });

  return {
    updateCustomerDetails,
    loading,
    error,
  };
};

export const useUpdatePrimaryPhoneNumber = (
  callback?: (data: UpdatePrimaryPhoneNumberMutation) => void,
) => {
  const [updatePrimaryPhoneNumber, { loading, error }] = useMutation(
    UpdatePrimaryPhoneNumberDocument,
    {
      onCompleted: callback,
    },
  );

  return {
    updatePrimaryPhoneNumber,
    loading,
    error,
  };
};

export const useUpdatePassword = (completedCallback?: (data: UpdatePasswordMutation) => void) => {
  const [updatePassword, { loading, error }] = useMutation(UpdatePasswordDocument, {
    onCompleted: completedCallback,
  });

  return {
    updatePassword,
    loading,
    error,
  };
};
